import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import format from 'date-fns/format';
import distanceInWords from 'date-fns/distance_in_words';
import differenceInCalendarDays from 'date-fns/difference_in_calendar_days';

import { TextButton } from '@zillow/constellation';

import { UserSelectors, AppSelectors } from 'app/shared/selectors';
import BillingActions from 'app/shared/actions/BillingActions';
import BillingConstants from 'app/shared/constants/BillingConstants';
import BillingInvoicePage from 'app/shared/components/Billing/BillingInvoicePage';
import numberUtils from 'app/shared/utils/numberUtils';
import PropertyNameList from 'app/shared/containers/BillingPage/PropertyNameList';

const dateGrouping = (rowData) => {
    const date = new Date(rowData.invoiceDate);
    return `${date.getFullYear()}_${date.getMonth()}`;
};

const dateHeaderRenderer = (data) => {
    const { groupRows } = data;
    const invoiceDate = new Date(groupRows[0].invoiceDate);
    const now = new Date();
    let oldestDate = new Date();
    groupRows.forEach((row) => {
        const rowDate = new Date(row.invoiceDate);
        if (rowDate.getTime() < oldestDate.getTime()) {
            oldestDate = rowDate;
        }
    });
    oldestDate.setDate(oldestDate.getDate() + 30);
    const displayLateNotice = differenceInCalendarDays(oldestDate, now) < 0;
    return (
        <React.Fragment>
            <span className="TableMFH-grouping-header">
                {format(invoiceDate, 'MMMM YYYY')}
            </span>
            {displayLateNotice && (
                <span className="TableMFH-grouping-header-error">
                    {`${distanceInWords(now, oldestDate)} past due`}
                </span>
            )}
        </React.Fragment>
    );
};

const invoiceRenderer = invoice => (invoice.invoiceType !== BillingConstants.INVOICE_TYPES.NUCLEUS_LISTING
    ? (
        <Link as="a" component={TextButton} to={`/rental-manager/billing/invoice/${invoice.invoiceNumber}?prev=due`}>
            {invoice.invoiceNumber}
        </Link>
    )
    : (
        <span>
            {invoice.invoiceNumber}
        </span>
    ));

const fullColumnConfig = [
    {
        width: 2,
        dataKey: 'invoiceDate',
        header: 'Date',
        renderer: invoice => invoice && invoice.invoiceDate && (new Date(invoice.invoiceDate)).toLocaleDateString(),
    },
    {
        width: 2,
        dataKey: 'invoiceNumber',
        header: 'Invoice No.',
        renderer: invoiceRenderer,
    },
    {
        width: 2,
        dataKey: 'propertyNames',
        header: 'Properties',
        renderer: invoice => <PropertyNameList invoice={invoice} />,
    },
    {
        width: 2,
        dataKey: 'amount',
        header: 'Amount',
        renderer: invoice => numberUtils.asCurrency(invoice.amount, '$0,0.00'),
        align: 'right',
    },
    {
        width: 2,
        dataKey: 'balance',
        header: 'Balance*',
        renderer: invoice => numberUtils.asCurrency(invoice.balance, '$0,0.00'),
        align: 'right',
    },
];

const mobileColumnConfig = [
    {
        width: 3,
        dataKey: 'invoiceDate',
        header: 'Date',
        renderer: invoice => invoice && invoice.invoiceDate && (new Date(invoice.invoiceDate)).toLocaleDateString(),
    },
    {
        width: 4,
        dataKey: 'invoiceNumber',
        header: 'Invoice No.',
        renderer: invoiceRenderer,
    },
    {
        width: 3,
        dataKey: 'balance',
        header: 'Balance*',
        renderer: invoice => numberUtils.asCurrency(invoice.balance, '$0,0.00'),
        align: 'right',
    },
];

const mapStateToProps = state => ({
    user: UserSelectors.makeUser(state),
    loadingState: state.billing.invoicesDue.loadingState,
    errorMsg: state.billing.invoice.currentInvoiceErrorMessage,
    filters: state.billing.invoicesDue.filters,
    invoices: state.billing.invoicesDue.invoices,
    hasMoreData: state.billing.invoicesDue.hasMoreData,
    isScreenMobile: AppSelectors.makeIsScreenMobile(state),
    isFilterEnabled: state.billing.invoicesDue.isFilterEnabled,
    isPaymentDue: true,
    fullColumnConfig,
    mobileColumnConfig,
    groupingFunction: dateGrouping,
    groupingHeaderRenderer: dateHeaderRenderer,
    emptyMessage: BillingConstants.EMPTY_NOTIFICATIONS.NO_DUE_INVOICES,
    title: 'Payment Due',
    cookieEnabledServices: AppSelectors.makeCookieEnabledServices(state),
});

const mapDispatchToProps = {
    fetchBillingData: BillingActions.invoicesDue.fetchBillingData,
    fetchPropertyData: BillingActions.invoicesDue.fetchPropertyData,
    clearInvoices: BillingActions.invoicesDue.clearInvoices,
    setFilters: BillingActions.invoicesDue.setFilters,
};

export { BillingInvoicePage as PaymentDuePage };
export default connect(mapStateToProps, mapDispatchToProps)(BillingInvoicePage);
